// Uploads a file to a presigned PUT URL minted by basket-api. Throws on failure
// (non-2xx, or network/CORS reject) so callers commit no token for an unstored photo.
async function uploadFileToPresignedUrl(file: File, uploadUrl: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
  } catch (e) {
    /* `fetch` only natively rejects with a TypeError on network / CORS-preflight
       failures (no HTTP status is observed). Surface a clearer message; rethrow
       anything else so unexpected errors aren't masked. */
    if (e instanceof TypeError) {
      throw new Error(
        'Photo upload was blocked before reaching storage. Please try again, and if it persists contact support.',
      );
    }
    throw e;
  }

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error(
        'Photo upload was rejected by storage (403). Please try again, and if it persists contact support.',
      );
    }
    throw new Error(`Image upload failed with status ${response.status}`);
  }
}

export interface ImageUploadSlot {
  objectUrl: string;
  uploadUrl: string;
  uploadUrlValidForSeconds: number;
}

export interface ImageUploadResult {
  objectUrl: string;
  ok: boolean;
}

/** PUTs each file to its slot and reports per-photo outcome (aligned to input
 * order). Never throws on a failed PUT — callers keep the ok photos and retry
 * the rest; all uploads are attempted before resolving. */
export async function uploadFilesToPresignedSlots(
  files: File[],
  slots: ImageUploadSlot[],
): Promise<ImageUploadResult[]> {
  if (files.length !== slots.length) {
    throw new Error('Number of files must match number of upload slots');
  }

  const settled = await Promise.allSettled(
    files.map((file, i) => uploadFileToPresignedUrl(file, slots[i].uploadUrl)),
  );

  return settled.map((result, i) => ({
    objectUrl: slots[i].objectUrl,
    ok: result.status === 'fulfilled',
  }));
}
